import { useCallback, useEffect, useState } from 'react';
import type { CurrentUser, Order, OrderStatus } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { formatMoney, makeT, orderStatusLabel } from '../i18n';
import { haptic } from '../tg';

export function MyOrders({ user, onBack }: { user: CurrentUser; onBack: () => void }) {
  const t = makeT(user.locale);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [scope, setScope] = useState<'active' | 'completed'>('active');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setOrders(await api.get<Order[]>(`/api/orders?scope=${scope}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('networkError'));
    }
  }, [scope, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function cancel(id: number) {
    haptic('warning');
    try {
      await api.post(`/api/orders/${id}/cancel`, {});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('actionFailed'));
    }
  }

  const label = (s: OrderStatus) => orderStatusLabel(s, user.locale);

  return (
    <main className="page">
      <h1 className="h1">{t('orders')}</h1>

      <div style={{ display: 'flex', gap: 'var(--s-2)', marginBottom: 'var(--s-4)' }}>
        {(['active', 'completed'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScope(s)}
            style={{
              minHeight: 'var(--tap-min)',
              padding: '0 var(--s-4)',
              borderRadius: 'var(--r-full)',
              border: '1px solid var(--c-border-input)',
              background: scope === s ? 'var(--c-primary-subtle)' : 'var(--c-surface)',
              color: scope === s ? 'var(--c-primary-text)' : 'var(--c-text)',
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            {s === 'active' ? t('activeOrders') : t('completedOrders')}
          </button>
        ))}
      </div>

      {error && (
        <p role="alert" style={{ color: 'var(--c-danger)' }}>
          {error}
        </p>
      )}

      {orders === null ? (
        <p className="muted">…</p>
      ) : orders.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>{t('noOrders')}</p>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%', marginTop: 'var(--s-3)' }}
            onClick={onBack}
          >
            {t('goToCatalog')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--s-3)' }}>
          {orders.map((o) => (
            <article key={o.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>#{o.public_number}</strong>
                <strong>
                  {formatMoney(o.total, user.locale)} {t('sum')}
                </strong>
              </div>

              <p
                style={{
                  margin: 'var(--s-2) 0 0',
                  color: o.status === 'delivered' ? 'var(--c-success)'
                    : o.status === 'cancelled' || o.status === 'failed' ? 'var(--c-danger)'
                    : 'var(--c-text)',
                }}
              >
                {label(o.status)}
              </p>

              <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
                {o.slot_date}, {o.slot_start}–{o.slot_end} · {o.address_text}
              </p>

              <ul style={{ margin: 'var(--s-3) 0 0', padding: 0, listStyle: 'none' }}>
                {o.items.map((i) => (
                  <li key={i.id} className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                    {i.name} × {i.qty}
                  </li>
                ))}
              </ul>

              {/* Кнопка отмены доступна только до выезда курьера — решает сервер */}
              {o.can_cancel && (
                <button
                  type="button"
                  onClick={() => void cancel(o.id)}
                  style={{
                    marginTop: 'var(--s-3)',
                    minHeight: 'var(--tap-min)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--c-danger)',
                    font: 'inherit',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {t('cancelOrder')}
                </button>
              )}
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
